// src/components/Sidebar/SidebarLeft.jsx

import React from 'react'
import {
  BookOpenIcon,
  FolderIcon,
  SquaresPlusIcon,
  ClockIcon,
} from '@heroicons/react/24/outline'
import { useSidebar } from '../../contexts/SidebarContext'

const TABS = [
  {
    id: 'library',
    label: 'My Recipes',
    icon: BookOpenIcon,
    component: React.lazy(() => import('./RecipeLibrary')),
  },
  {
    id: 'folders',
    label: 'Collections',
    icon: FolderIcon,
    component: React.lazy(() => import('./FolderManager')),
  },
  {
    id: 'menus',
    label: 'Menus',
    icon: SquaresPlusIcon,
    component: React.lazy(() => import('./MenuOrganizer')),
  },
  {
    id: 'versions',
    label: 'History',
    icon: ClockIcon,
    component: React.lazy(() => import('./VersionControl')),
  },
]

export default function SidebarLeft() {
  const { activeSidebarTab, setActiveSidebarTab, sidebarTabCounts } = useSidebar()
  // 🔥 ANDROID BACK BUTTON INTEGRATION
  // Expose a global function so MainActivity.java can trigger sidebar close
  if (typeof window !== "undefined") {
    window.closeLeftSidebar = () => {
      setActiveSidebarTab(null);   // null = closed
    };
  }
  const currentTab = TABS.find(t => t.id === activeSidebarTab) || TABS[0]
  const ActiveComp = currentTab.component

  return (
    <aside
      className="
        flex flex-col h-full
        w-full flex-none       /* ← full-width on mobile and up */
        bg-very-light-gray text-charcoal
      "
      aria-label="Sidebar navigation"
    >
      {/* ─── Icon Tabs ─────────────────────────── */}
      <nav
        role="tablist"
        className="flex justify-between md:justify-around items-center border-b border-light-gray py-2 px-2"
      >
        {TABS.map(tab => {
          const isActive = tab.id === activeSidebarTab
          const Icon = tab.icon

          return (
            <button
              key={tab.id}
              role="tab"
              aria-selected={isActive}
              aria-label={tab.label}
              title={tab.label}
              onClick={() => setActiveSidebarTab(tab.id)}
              className={`
                relative flex items-center
                ${isActive ? 'px-4' : 'px-2'} py-3 md:py-3 rounded transition
                focus:outline-none focus-visible:ring-2 focus-visible:ring-rich-gold
                ${isActive
                  ? 'bg-white text-deep-blue shadow-inner ring-2 ring-rich-gold'
                  : 'text-charcoal hover:bg-soft-yellow/40 hover:text-rich-gold'}
              `}
            >
              <Icon className="w-5 h-5" />

              {/* only show label on active tab, truncated if too long */}
              {isActive && (
                <span className="ml-2 max-w-[80px] truncate whitespace-nowrap text-sm font-medium">
                  {tab.label}
                </span>
              )}

              {/* badge count */}
              {sidebarTabCounts?.[tab.id] > 0 && (
                <span className="absolute -top-1 -right-1 text-xs bg-rich-gold text-white rounded-full px-1">
                  {sidebarTabCounts[tab.id]}
                </span>
              )}
            </button>
          )
        })}
      </nav>

      {/* ─── Content Panel ────────────────────────── */}
      <div
        role="tabpanel"
        aria-labelledby={`tab-${currentTab.id}`}
        className="flex-1 overflow-y-auto"
      >
        <React.Suspense fallback={
          <div className="p-unit text-sm text-slate-gray text-center">
            {/* you could swap this for a spinner or skeleton component */}
            Loading…
          </div>
        }>
          <ActiveComp />
        </React.Suspense>
      </div>
    </aside>
  )
}
